import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import * as Models from '../_shared/models/models-index';
import {
    AuthenticationService,
    LocalCacheService,
    SpinnerService,
    ConfigurationService,
    IndexDbStorageService,
    EntraLoginService,
    RuntimeConfigService,
    IAuthProvider,
    IClientSsoProvider
} from '../_shared/services/services-index';
import { Enums, IEnums } from '../_shared/enums/enums';
import { environment } from '../../environments/environment';
import { Store } from '@ngrx/store';
import { AppState } from '../_store/app-state.model';
import { AppActions } from '../_store/action-types';
import { tap, finalize, catchError, switchMap, filter } from 'rxjs/operators';
import { throwError, of, Subscription, EMPTY } from 'rxjs';
import { FilterSelectors, FilterActions } from '../_shared/filter/store';
import { AppSelectors } from '../_store/selector-types';
import { FilterBarService } from '../_shared/filter/filter-bar.service';
import { ILogin } from './login.model';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { B2CLoginService } from '../_shared/services/authentication/b2c-login.service';
import { IAppConfig } from '../_shared/services/config/runtime-config.service';

@Component({
    templateUrl: './login-v2.component.html',
    styleUrls: ['./login-v2.component.scss']
})

export class LoginV2Component implements OnInit, OnDestroy {
    private _subscription = new Subscription();
    message = '';
    loginForm: UntypedFormGroup;
    isWorking = false;
    runtimeConfig: IAppConfig;
    serverError = '';

    // Auth provider properties
    authProviders: IAuthProvider[] = [];
    b2cEnabled = false;
    
    // Debug properties
    isDebugMode = false;
    debugInfo: any = {};

    get username(): AbstractControl {
        return this.loginForm.controls['username'];
    }

    constructor(
        private store: Store<AppState>,
        private authService: AuthenticationService,
        private router: Router,
        private spinner: SpinnerService,
        private cache: LocalCacheService,
        private filterBarService: FilterBarService,
        private configService: ConfigurationService,
        private indexDbStorageService: IndexDbStorageService,
        private fb: UntypedFormBuilder,
        private entraLoginService: EntraLoginService,
        private runtimeConfigService: RuntimeConfigService,
        private b2cLoginService: B2CLoginService
    ) {
        
    }

    ngOnInit() {
        this.loginForm = this.fb.group({
            username: ['', [Validators.required, Validators.email]]
        });

        this.store.dispatch(AppActions.resetAuth());
        this.store.dispatch(FilterActions.resetAllReportViewFilters());
        this.filterBarService.clearFiltersLoaded();
        this.runtimeConfig = this.runtimeConfigService.config;
        console.log('Runtime config:', this.runtimeConfig);

        // Get available authentication providers from runtime configuration
        this.loadAuthProviders();
        
        // Initialize debug information
        this.initializeDebugMode();
    }
    
    /**
     * Initialize debug mode and debug information
     */
    private initializeDebugMode(): void {
        // Enable debug mode in non-production environments
        this.isDebugMode = !environment.production;
        
        if (this.isDebugMode) {
            // Collect debug information about auth configuration
            this.debugInfo = {
                hasB2CConfig: !!this.runtimeConfig.auth?.azureB2C,
                hasEntraConfig: !!this.runtimeConfig.shiftSSOClientId,
                authProviders: this.authProviders.map(p => p.id),
                environment: environment.production ? 'production' : 'development',
                b2cEnabled: this.b2cEnabled
            };
            
            // Add B2C config details if available
            if (this.runtimeConfig.auth?.azureB2C) {
                this.debugInfo.b2c = {
                    tenant: this.runtimeConfig.auth.azureB2C.tenant,
                    policy: this.runtimeConfig.auth.azureB2C.policy
                };
            }
            
            // Log auth configuration for debugging
            console.log('[Debug] Auth Configuration:', this.debugInfo);
        }
    }

    /**
     * Loads authentication providers from runtime configuration
     */
    private loadAuthProviders(): void {
        // Get sorted list of enabled auth providers
        this.authProviders = this.runtimeConfigService.enabledAuthProviders;

        // Check if B2C direct login is enabled
        this.b2cEnabled = !!this.runtimeConfig.auth?.azureB2C?.enabled;
    }

    ngOnDestroy() {
        this._subscription.unsubscribe();
    }

    /**
     * Handles provider login based on provider type
     */
    providerLogin(provider: IAuthProvider): void {
        switch (provider.id) {
            case 'entraId':
                this.microsoftEntraLogin();
                break;
            case 'azureB2C':
                // this.azureB2CLogin();
                break;
            default:
                // Must be a client SSO provider
                this.ssoProviderLogin((provider as IClientSsoProvider).provider);
                break;
        }
    }

    /**
     * Initiate Microsoft Entra ID login flow using the dedicated Entra MSAL instance
     */
    microsoftEntraLogin(): void {
        console.log('Initiating Entra ID login flow...');
        this.spinner.show();
        
        try {
            this.entraLoginService.loginRedirect()
                .catch(error => {
                    console.error('Entra ID login redirect failed:', error);
                    this.spinner.hide();
                    this.message = 'Error initiating Entra ID login. Please try again.';
                });
                
            if (this.isDebugMode) {
                console.log('[Debug] Entra ID login redirect initiated');
            }
        } catch (error) {
            console.error('Error calling Entra login service:', error);
            this.spinner.hide();
            this.message = 'Error initiating Entra ID login. Please try again.';
        }
    }

    /**
     * Initiate Azure B2C login flow using the dedicated B2C MSAL instance
     */
    // azureB2CLogin(): void {
    //     console.log('Initiating Azure B2C login flow...');
    //     this.spinner.show();
        
    //     try {
    //         this.b2cLoginService.loginRedirect()
    //             .catch(error => {
    //                 console.error('Azure B2C login redirect failed:', error);
    //                 this.spinner.hide();
    //                 this.message = 'Error initiating Azure B2C login. Please try again.';
    //             });
                
    //         if (this.isDebugMode) {
    //             console.log('[Debug] Azure B2C login redirect initiated');
    //         }
    //     } catch (error) {
    //         console.error('Error calling B2C login service:', error);
    //         this.spinner.hide();
    //         this.message = 'Error initiating Azure B2C login. Please try again.';
    //     }
    // }

    ssoProviderLogin(provider: string): void {
        this.cache.purgeAllLocalCaches();
        this.indexDbStorageService.purgeCache().subscribe();
        this.message = '';
        this.spinner.show();

        // TODO: Keep an eye on it - this is a workaround for the client SSO redirect issue
        if (!!this.runtimeConfig.clientSSOIssuer)
            window.location.href = this.runtimeConfig.webUri + '/auth/login';
        else {
            console.log('Redirecting to client SSO URL:', this.runtimeConfig.clientSSOUrl);
            window.location.href = this.runtimeConfig.clientSSOUrl;
            return;
        }

        this._subscription.add(
            this.authService.ssoProviderLogin(provider)
                .pipe(
                    tap(response => {
                        // For clients like Mercury Marine and Infiniti, we need to redirect to the client IDP with the AuthN request
                        if (response.authRequest) {
                            // We received an AuthN request that needs to be submitted to the client IDP
                            const clientSsoUrl = this.runtimeConfig.clientSSOUrl;
                            if (clientSsoUrl) {
                                // Redirect to the client IDP with the AuthN request
                                window.location.href = `${clientSsoUrl}?SAMLRequest=${encodeURIComponent(response.authRequest)}`;
                            } else {
                                this.spinner.hide();
                                this.message = 'Error: Client SSO URL is not configured.';
                            }
                        }
                    }),
                    catchError(err => {
                        this.spinner.hide();
                        console.error(err);
                        this.message = 'Error initiating SSO provider login.';
                        return EMPTY;
                    })
                ).subscribe()
        );
    }

    /**
     * Handle B2C login with email hint using the dedicated B2C MSAL instance
     */
    login(): void {
        this.cache.purgeAllLocalCaches();
        this.indexDbStorageService.purgeCache().subscribe();
        this.message = '';

        if (!this.loginForm.valid) {
            this.message = 'Please enter a valid email address';
            return;
        }

        this.isWorking = true;
        this.spinner.show();

        try {
            if (this.isDebugMode) {
                console.log('Azure B2C login with hint initiated for email:', this.loginForm.value.username);
            }
            
            // Use the login hint approach with dedicated B2C MSAL instance
            // This is more secure as we never collect or handle passwords
            this.b2cLoginService.loginWithHint(
                this.loginForm.value.username
            ).catch(error => {
                this.spinner.hide();
                this.isWorking = false;
                console.error('Error initiating B2C login with hint:', error);
                this.message = 'An error occurred during login. Please try again.';
                
                if (this.isDebugMode) {
                    this.debugInfo.loginError = {
                        message: error.message,
                        name: error.name,
                        timestamp: new Date().toISOString()
                    };
                }
            });

            // No need to handle success here since the callback component will handle it
            // Just keep showing the spinner as the redirect happens
        } catch (error) {
            this.spinner.hide();
            this.isWorking = false;
            console.error('Error calling B2C login service:', error);
            this.message = 'An error occurred during login. Please try again.';
            
            if (this.isDebugMode) {
                this.debugInfo.loginError = {
                    message: error.message,
                    name: error.name,
                    timestamp: new Date().toISOString()
                };
            }
        }
    }
}
